import { z } from 'zod';
import { TypedApiClient } from '../index';
import { SaleResponseSchema, SaleResponse } from './sales';
import { schemas } from '../../generated/api-client';
import { apiPath } from '../path';
import { idWindowPageSchema, type IdWindowPage, type IdWindowParams } from '../id-window';

// Import schemas from generated (per data transformation architecture)
const {
  ClientFirestoreResponse: ClientResponseDtoGenerated,
  CreateClientDto,
  ClientPunchCardResponseDto,
  CreateClientBonusAdjustmentDto,
  ClientBonusAdjustmentResponseDto,
} = schemas;

// Export schemas - use generated to ensure sync with backend
export const ClientResponseSchema = ClientResponseDtoGenerated;
export const ClientPunchCardResponseSchema = ClientPunchCardResponseDto;
export const CreateClientBonusAdjustmentSchema = CreateClientBonusAdjustmentDto;
export const ClientBonusAdjustmentResponseSchema = ClientBonusAdjustmentResponseDto;

// Stats schema - not in generated (custom endpoint), keep manual definition
export const ClientStatsResponseSchema = z.object({
  total: z.number(),
  new: z.number(),
  active: z.number(),
}).passthrough();

// Create schema - use generated
export const CreateClientSchema = CreateClientDto;

// Type exports
export type ClientResponse = z.infer<typeof ClientResponseSchema>;
export type ClientPunchCardResponse = z.infer<typeof ClientPunchCardResponseSchema>;
export type ClientStatsResponse = z.infer<typeof ClientStatsResponseSchema>;
export type CreateClient = z.infer<typeof CreateClientSchema>;
export type CreateClientBonusAdjustment = z.infer<typeof CreateClientBonusAdjustmentSchema>;
export type ClientBonusAdjustmentResponse = z.infer<typeof ClientBonusAdjustmentResponseSchema>;

// API factory function
export function createClientsApi(client: TypedApiClient) {
  return {
    getClients: (params?: {
      limit?: number;
      offset?: number;
      registrationDateFrom?: string;
      registrationDateTo?: string;
      lastPurchaseDateFrom?: string;
      lastPurchaseDateTo?: string;
    }): Promise<ClientResponse[]> => {
      return client.get('/clients', {
        params: params as Record<string, unknown>,
        responseSchema: z.array(ClientResponseSchema),
      });
    },

    /**
     * ATL-1477: one snapshot window of `/clients` — see `../id-window.ts`. Use it through
     * `readWholeList`, never directly: a single window is not the list.
     */
    getClientsWindow: (
      params: {
      registrationDateFrom?: string;
      registrationDateTo?: string;
      lastPurchaseDateFrom?: string;
      lastPurchaseDateTo?: string;
      storeId?: string;
      includeDeleted?: 'true';
      } & IdWindowParams,
    ): Promise<IdWindowPage<z.infer<typeof ClientResponseSchema>>> => {
      return client.get('/clients/window', {
        params: params as Record<string, unknown>,
        responseSchema: idWindowPageSchema(ClientResponseSchema),
      });
    },

    searchClients: (query: string, limit?: number): Promise<ClientResponse[]> => {
      return client.get('/clients/search', {
        params: { q: query, ...(limit != null ? { limit } : {}) },
        responseSchema: z.array(ClientResponseSchema),
      });
    },

    createClient: (data: CreateClient): Promise<ClientResponse> => {
      CreateClientSchema.parse(data);
      return client.post('/clients', {
        data,
        responseSchema: ClientResponseSchema,
      });
    },

    getClientById: (id: string): Promise<ClientResponse> => {
      return client.get(apiPath`/clients/${id}`, {
        responseSchema: ClientResponseSchema,
      });
    },

    getClientPurchaseHistory: (
      id: string,
      params?: { limit?: number; offset?: number },
    ): Promise<SaleResponse[]> => {
      return client.get(apiPath`/clients/${id}/purchase-history`, {
        params: params as Record<string, unknown>,
        responseSchema: z.array(SaleResponseSchema),
      });
    },

    updateClient: (id: string, data: Partial<CreateClient>): Promise<ClientResponse> => {
      return client.put(apiPath`/clients/${id}`, {
        data,
        responseSchema: ClientResponseSchema,
      });
    },

    /**
     * ATL-1445 stage 2c — a manual bonus change as a DELTA, applied at most once per operationId
     * (the Firestore operation document id). A replay answers `status: 'replayed'`.
     */
    applyBonusAdjustment: (
      id: string,
      data: CreateClientBonusAdjustment,
    ): Promise<ClientBonusAdjustmentResponse> => {
      return client.post(apiPath`/clients/${id}/bonus-adjustments`, {
        data,
        responseSchema: ClientBonusAdjustmentResponseSchema,
      });
    },

    getClientPunchCardInfo: (id: string): Promise<ClientPunchCardResponse> => {
      return client.get(apiPath`/clients/${id}/punch-card`, {
        responseSchema: ClientPunchCardResponseSchema,
      });
    },

    deleteClient: (id: string) => {
      return client.delete(apiPath`/clients/${id}`, {
        responseSchema: z.object({ message: z.string() }).passthrough(),
      });
    },

    getClientStats: (params?: {
      registrationDateFrom?: string;
      registrationDateTo?: string;
      lastPurchaseDateFrom?: string;
      lastPurchaseDateTo?: string;
    }): Promise<ClientStatsResponse> => {
      return client.get('/clients/stats', {
        params: params as Record<string, unknown>,
        responseSchema: ClientStatsResponseSchema,
      });
    },
  };
}
